import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useParams, useNavigate } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'
import { Printer, ArrowLeft, Loader2, AlertCircle, XCircle, CheckCircle2, ChevronDown } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QuizScript } from '@quebon/shared-learning'
import {
  QuizRenderer,
  LAYOUT,
  STEP4_LAYOUT,
  PAPER_SIZES,
  BLEED_SIZE_MM,
  mmToPx,
  getPageLayout,
  toQuizData,
  PageHeader,
  StepHeader,
  PageFooter,
  ChapterNumber,
  TitleQueture,
  TitleStepSvg,
  PrintQuizItem,
  ImageScaleMultiplierProvider,
  AdditionalImageScalesProvider,
  OptionImageScaleProvider,
  Step4Section,
  MemoPage,
  CoverPage,
  ModuleSummaryPage,
  TocPage,
  type TocModuleData,
  type TocLesson,
  type StepPage,
  usePageSplitter,
  useQuizMeasurement,
  LogoPage,
  BlankPage,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'
import { printBookApi, type PrintBookDetail, type PrintBookLessonSettings, type PrintBookPagesUpdateRequest } from '@/api/printBook'
import { getQuizScale, getQuizSpacing, getQuizAdditionalScalesMap, getQuizOptionScale } from './LessonPreviewSettingsPanel'
import { calculateBlockQuizStartPage } from './fullBookPreview/calculateBlockQuizStartPage'
import { collectInvalidQuizzes } from './fullBookPreview/validateSampleVars'
import { InvalidQuizList } from './fullBookPreview/InvalidQuizList'

// ─── Types ──────────────────────────────────────────────────────────

interface LessonRenderData {
  lessonId: number
  lessonName: string
  bareTitle: string | null
  grade: string | null
  chapterNumber: string
  bookLabel: string
  unitLabel: string
  unitName: string
  steps: QuizScript[][]
  step4Questions: string[]
  step4Quizzes: QuizScript[]   // 검증용 원본 (step4Questions 는 title 전용)
  settings: PrintBookLessonSettings | null
  hasMemoPage: boolean
}

interface LessonMeasurement {
  contentPageCount: number      // step4 별도 페이지 포함, memo 페이지 제외
  step4InlineFits: boolean
  stepPages: StepPage[]
  measuredHeights: number[]
}

interface LessonLayout {
  startPage: number
  contentPageCount: number
  needsMemoPage: boolean
  totalPageCount: number        // contentPageCount + (needsMemoPage ? 1 : 0)
}

// ─── Utilities ──────────────────────────────────────────────────────

function parseGradeField(raw: string): { grade: string; unitLabel: string; unitName: string } | null {
  const cleaned = raw.replace(/^\([^)]*\)\s*/, '')
  const match = cleaned.match(/\[(\S+)\s+(\d+)-(\d+)-(\d+)\]\s*(.+)/)
  if (!match) return null
  return {
    grade: `${match[1]} ${match[2]}-${match[3]}`,
    unitLabel: `${match[4]}단원`,
    unitName: match[5].trim(),
  }
}

const COVER_PAGE_COUNT = 1
const TOC_PAGE_COUNT = 2
const MODULE_SUMMARY_PAGE_COUNT = 2

// ─── Image Cover Page ───────────────────────────────────────────────

function ImageCoverPage({
  src,
  bleedEnabled,
  pageWidthPx,
  pageHeightPx,
  totalPageWidthPx,
  totalPageHeightPx,
  bleedPxAlways,
}: {
  src: string
  bleedEnabled: boolean
  pageWidthPx: number
  pageHeightPx: number
  totalPageWidthPx: number
  totalPageHeightPx: number
  bleedPxAlways: number
}) {
  const containerW = bleedEnabled ? totalPageWidthPx : pageWidthPx
  const containerH = bleedEnabled ? totalPageHeightPx : pageHeightPx
  return (
    <div
      className="print-page"
      style={{
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        width: containerW,
        height: containerH,
        background: '#ffffff',
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          display: 'block',
          width: totalPageWidthPx,
          height: totalPageHeightPx,
          marginLeft: bleedEnabled ? 0 : -bleedPxAlways,
          marginTop: bleedEnabled ? 0 : -bleedPxAlways,
        }}
      />
    </div>
  )
}

// ─── Lesson Measurer (off-screen, measurement only) ──────────────────

function LessonMeasurer({
  lesson,
  pageWidthPx,
  pageHeightPx,
  onMeasured,
}: {
  lesson: LessonRenderData
  pageWidthPx: number
  pageHeightPx: number
  onMeasured: (lessonId: number, m: LessonMeasurement) => void
}) {
  const measureRef = useRef<HTMLDivElement>(null)
  const allQuizzes = useMemo(() => lesson.steps.flat(), [lesson.steps])

  const { measuredHeights, isMeasuring } = useQuizMeasurement(
    measureRef,
    allQuizzes,
    ['A4', 'portrait', pageWidthPx, JSON.stringify(lesson.settings ?? null)]
  )
  const stepPages = usePageSplitter(lesson.steps, measuredHeights, pageHeightPx)
  const gutterPx = 0

  const step4InlineFits = useMemo(() => {
    if (stepPages.length === 0) return false
    const lastPage = stepPages[stepPages.length - 1]
    let usedHeight = 0
    if (lastPage.isFirstOfStep) {
      if (lastPage.stepIdx === 0) {
        usedHeight += LAYOUT.header.totalHeight + LAYOUT.step1FirstPaddingTop + 51 + LAYOUT.quizTopPadding.afterStepTitle
      } else {
        usedHeight += (LAYOUT.step2.barHeight + LAYOUT.step2.curveHeight) + LAYOUT.quizTopPadding.step23FirstPage
      }
    } else {
      if (lastPage.stepIdx === 0) {
        usedHeight += LAYOUT.header.barHeight + LAYOUT.quizTopPadding.continuationGap
      } else {
        usedHeight += LAYOUT.step2.barHeight + LAYOUT.quizTopPadding.continuationGap
      }
    }
    const globalOffset = lesson.steps.slice(0, lastPage.stepIdx).reduce((sum, s) => sum + s.length, 0)
    lastPage.quizzes.forEach((_: QuizScript, i: number) => {
      const quizIdx = globalOffset + (lastPage.quizStartNum - 1) + i
      usedHeight += measuredHeights[quizIdx] || 400
    })
    const footerSpace = 72 + LAYOUT.footer.bottomGap
    const availableSpace = pageHeightPx - usedHeight - footerSpace
    return availableSpace >= STEP4_LAYOUT.inlineHeight
  }, [stepPages, measuredHeights, pageHeightPx, lesson.steps])

  const contentPageCount = useMemo(() => {
    let count = stepPages.length
    if (lesson.step4Questions.length > 0 && !step4InlineFits && stepPages.length > 0) count++
    return count
  }, [stepPages, step4InlineFits, lesson.step4Questions.length])

  const reportedKeyRef = useRef<string>('')
  useEffect(() => {
    if (isMeasuring) return
    if (measuredHeights.length === 0) return
    if (!measuredHeights.some(h => h !== 400 && h > 0)) return
    const key = `${contentPageCount}|${step4InlineFits}|${measuredHeights.join(',')}`
    if (reportedKeyRef.current === key) return
    reportedKeyRef.current = key
    onMeasured(lesson.lessonId, {
      contentPageCount,
      step4InlineFits,
      stepPages,
      measuredHeights,
    })
  }, [isMeasuring, measuredHeights, contentPageCount, step4InlineFits, stepPages, lesson.lessonId, onMeasured])

  if (allQuizzes.length === 0) return null

  return (
    <div
      ref={measureRef}
      aria-hidden
      style={{
        position: 'absolute',
        left: -9999,
        top: 0,
        width: `${pageWidthPx - LAYOUT.pageSidePadding * 2 - gutterPx}px`,
        visibility: 'hidden',
        lineHeight: 1.6,
      }}
    >
      {allQuizzes.map((script, i) => {
        let stepIdx = 0, quizIdx = i
        let cumulative = 0
        for (let s = 0; s < lesson.steps.length; s++) {
          if (i < cumulative + lesson.steps[s].length) {
            stepIdx = s
            quizIdx = i - cumulative
            break
          }
          cumulative += lesson.steps[s].length
        }
        const scale = getQuizScale(lesson.settings, stepIdx, quizIdx)
        const spacing = getQuizSpacing(lesson.settings, stepIdx, quizIdx)
        const additionalMap = getQuizAdditionalScalesMap(
          lesson.settings, stepIdx, quizIdx,
          toQuizData(script).additionalImages?.length ?? 0,
        )
        const optionScale = getQuizOptionScale(lesson.settings, stepIdx, quizIdx)

        const quizContent = (
          <QuizRenderer
            quizType={toQuizData(script).type}
            quizId={String(script.quizId ?? '')}
            quiz={toQuizData(script)}
            mode="print"
            number={i + 1}
            hideSubmitButton
            disableSound
            compact
          />
        )
        return (
          <div key={`measure-${lesson.lessonId}-${i}`} data-quiz-measure style={{ paddingBottom: `${spacing}px` }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <OptionImageScaleProvider value={optionScale}>
                <AdditionalImageScalesProvider value={additionalMap}>
                  {scale !== 1
                    ? <ImageScaleMultiplierProvider value={scale}>{quizContent}</ImageScaleMultiplierProvider>
                    : quizContent
                  }
                </AdditionalImageScalesProvider>
              </OptionImageScaleProvider>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Lesson Renderer (visible pages only, layout driven by parent) ──

function LessonRenderer({
  lesson,
  startPage,
  needsMemoPage,
  stepPages,
  step4InlineFits,
  pageHeightPx,
  totalPageWidthPx,
  totalPageHeightPx,
  bleedPx,
}: {
  lesson: LessonRenderData
  startPage: number
  needsMemoPage: boolean
  stepPages: StepPage[]
  step4InlineFits: boolean
  pageHeightPx: number
  totalPageWidthPx: number
  totalPageHeightPx: number
  bleedPx: number
}) {
  const { footerTop } = getPageLayout(pageHeightPx)

  if (lesson.steps.flat().length === 0) return null

  const contentPageCount =
    stepPages.length + (lesson.step4Questions.length > 0 && !step4InlineFits && stepPages.length > 0 ? 1 : 0)

  return (
    <>
      {stepPages.map((sp, pageIdx) => {
        const pageNumber = startPage + pageIdx
        const isEven = pageNumber % 2 === 0
        const isStep1 = sp.stepIdx === 0
        const isStep1FirstPage = isStep1 && sp.isFirstOfStep

        return (
          <div key={`lesson-${lesson.lessonId}-page-${pageIdx}`} className="print-page relative" style={{ width: `${totalPageWidthPx}px` }}>
            <div className="relative" style={{ height: totalPageHeightPx }}>
              {isStep1FirstPage ? (
                <>
                  <PageHeader isEven={isEven} bleedPx={bleedPx} />
                  <ChapterNumber number={lesson.chapterNumber} bleedPx={bleedPx} />
                  <TitleQueture
                    grade={lesson.grade?.split(' ').slice(-1)[0] || ''}
                    unitLabel={lesson.unitLabel}
                    unitName={lesson.unitName}
                    quetureTitle={
                      lesson.settings?.titleOverride?.trim()
                        ? lesson.settings.titleOverride
                        : (lesson.bareTitle || lesson.lessonName)
                    }
                    bleedPx={bleedPx}
                  />
                </>
              ) : isStep1 ? (
                <PageHeader isEven={isEven} bleedPx={bleedPx} />
              ) : (
                <div className="relative">
                  <StepHeader stepIndex={sp.stepIdx} isEven={isEven} bleedPx={bleedPx} />
                  {sp.isFirstOfStep && <TitleStepSvg stepIndex={sp.stepIdx} isAbsolute bleedPx={bleedPx} />}
                </div>
              )}

              <div style={{
                paddingTop: isStep1FirstPage ? LAYOUT.step1FirstPaddingTop + bleedPx : bleedPx,
                position: 'relative',
                zIndex: 1,
              }}>
                {isStep1FirstPage && <TitleStepSvg stepIndex={0} bleedPx={bleedPx} />}
                {(() => {
                  let quizPadTop: number
                  let quizMarginTop = 0
                  if (isStep1FirstPage) {
                    quizPadTop = LAYOUT.quizTopPadding.afterStepTitle
                  } else if (sp.isFirstOfStep) {
                    quizPadTop = LAYOUT.quizTopPadding.step23FirstPage
                  } else if (isStep1) {
                    quizPadTop = 0
                    quizMarginTop = -(LAYOUT.header.curveHeight - LAYOUT.quizTopPadding.continuationGap)
                  } else {
                    quizPadTop = LAYOUT.quizTopPadding.continuationGap - LAYOUT.step2.curveHeight
                  }
                  return (
                    <div style={{
                      paddingTop: quizPadTop,
                      marginTop: quizMarginTop,
                      paddingLeft: LAYOUT.pageSidePadding + bleedPx,
                      paddingRight: LAYOUT.pageSidePadding + bleedPx,
                    }}>
                      {sp.quizzes.map((script: QuizScript, i: number) => {
                        const quizIdx = sp.quizStartNum - 1 + i
                        const scale = getQuizScale(lesson.settings, sp.stepIdx, quizIdx)
                        const spacing = getQuizSpacing(lesson.settings, sp.stepIdx, quizIdx)
                        const additionalMap = getQuizAdditionalScalesMap(
                          lesson.settings, sp.stepIdx, quizIdx,
                          toQuizData(script).additionalImages?.length ?? 0,
                        )
                        const optionScale = getQuizOptionScale(lesson.settings, sp.stepIdx, quizIdx)
                        return (
                          <PrintQuizItem
                            key={`${lesson.lessonId}-${sp.stepIdx}-${sp.quizStartNum + i}`}
                            script={script}
                            number={sp.quizStartNum + i}
                            stepIdx={sp.stepIdx}
                            compact
                            imageScale={scale}
                            additionalImageScales={additionalMap}
                            optionImageScale={optionScale}
                            spacingOverride={spacing}
                          />
                        )
                      })}
                    </div>
                  )
                })()}
              </div>

              {pageIdx === stepPages.length - 1 && step4InlineFits && lesson.step4Questions.length > 0 && (
                <div style={{ position: 'absolute', bottom: totalPageHeightPx - (footerTop + bleedPx) + 20, left: bleedPx, right: bleedPx }}>
                  <Step4Section questions={lesson.step4Questions} />
                </div>
              )}

              <PageFooter pageNumber={pageNumber} isEven={isEven} bookLabel={lesson.bookLabel} footerTop={footerTop} bleedPx={bleedPx} />
            </div>
          </div>
        )
      })}

      {!step4InlineFits && lesson.step4Questions.length > 0 && stepPages.length > 0 && (() => {
        const step4PageNumber = startPage + stepPages.length
        const step4IsEven = step4PageNumber % 2 === 0
        return (
          <div key={`lesson-${lesson.lessonId}-step4`} className="print-page relative" style={{ width: `${totalPageWidthPx}px` }}>
            <div className="relative" style={{ height: totalPageHeightPx }}>
              <div className="relative">
                <StepHeader stepIndex={3} isEven={step4IsEven} bleedPx={bleedPx} />
                <TitleStepSvg stepIndex={3} isAbsolute bleedPx={bleedPx} />
              </div>
              <div style={{ paddingTop: LAYOUT.quizTopPadding.step23FirstPage + bleedPx, paddingLeft: bleedPx, paddingRight: bleedPx }}>
                <Step4Section questions={lesson.step4Questions} showTitle={false} />
              </div>
              <PageFooter pageNumber={step4PageNumber} isEven={step4IsEven} bookLabel={lesson.bookLabel} footerTop={footerTop} bleedPx={bleedPx} />
            </div>
          </div>
        )
      })()}

      {needsMemoPage && (
        <MemoPage
          pageNumber={startPage + contentPageCount}
          pageWidth={totalPageWidthPx - bleedPx * 2}
          pageHeight={totalPageHeightPx - bleedPx * 2}
          bleedPx={bleedPx}
        />
      )}
    </>
  )
}

// ─── Sequential Loader Hook ─────────────────────────────────────────

function useSequentialLessonLoader(
  bookDetail: PrintBookDetail | undefined,
) {
  const [lessonDataMap, setLessonDataMap] = useState<Map<number, LessonRenderData>>(new Map())
  const [loadedCount, setLoadedCount] = useState(0)
  const [isLoadingLesson, setIsLoadingLesson] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cancelled, setCancelled] = useState(false)
  const cancelRef = useRef(false)

  const allLessons = useMemo(() => {
    if (!bookDetail) return []
    const result: {
      lessonId: number
      lessonName: string
      bareTitle: string | null
      grade: string | null
      moduleIndex: number
      lessonIndex: number
      bookLabel: string
      settings: PrintBookLessonSettings | null
      hasMemoPage: boolean
    }[] = []

    bookDetail.modules.forEach((mod, mi) => {
      mod.lessons.forEach((lesson, li) => {
        let parsedSettings: PrintBookLessonSettings | null = null
        if (lesson.printBookLessonSettings) {
          try { parsedSettings = JSON.parse(lesson.printBookLessonSettings) } catch { /* ignore */ }
        }
        result.push({
          lessonId: lesson.lessonId,
          lessonName: lesson.lessonName || `레슨 ${li + 1}`,
          bareTitle: lesson.bareTitle,
          grade: lesson.grade,
          moduleIndex: mi,
          lessonIndex: li,
          bookLabel: bookDetail.bookLabel,
          settings: parsedSettings,
          hasMemoPage: lesson.hasMemoPage,
        })
      })
    })

    return result
  }, [bookDetail])

  const totalLessons = allLessons.length

  const cancel = useCallback(() => {
    cancelRef.current = true
    setCancelled(true)
    setIsLoadingLesson(false)
  }, [])

  useEffect(() => {
    if (!bookDetail || allLessons.length === 0 || cancelled) return

    let active = true
    cancelRef.current = false

    async function loadSequentially() {
      for (let i = 0; i < allLessons.length; i++) {
        if (cancelRef.current || !active) break

        const lesson = allLessons[i]
        setIsLoadingLesson(true)

        try {
          const [s1, s2, s3, s4] = await Promise.all([
            quizScriptApi.getContentQuizzes(lesson.lessonId, 'selected', false, true),
            quizScriptApi.getContentQuizzes(lesson.lessonId, 'block', false, true),
            quizScriptApi.getContentQuizzes(lesson.lessonId, 'applied', false, true),
            quizScriptApi.getContentQuizzes(lesson.lessonId, 'explanation', false, true),
          ])

          if (cancelRef.current || !active) break

          const parsed = lesson.grade ? parseGradeField(lesson.grade) : null

          const renderData: LessonRenderData = {
            lessonId: lesson.lessonId,
            lessonName: lesson.lessonName,
            bareTitle: lesson.bareTitle,
            grade: parsed?.grade || '',
            chapterNumber: String(lesson.lessonIndex + 1).padStart(2, '0'),
            bookLabel: lesson.bookLabel,
            unitLabel: parsed?.unitLabel || '',
            unitName: parsed?.unitName || '',
            steps: [s1, s2, s3],
            step4Questions: s4.map(q => q.title || ''),
            step4Quizzes: s4,
            settings: lesson.settings,
            hasMemoPage: lesson.hasMemoPage,
          }

          setLessonDataMap(prev => {
            const next = new Map(prev)
            next.set(lesson.lessonId, renderData)
            return next
          })
          setLoadedCount(i + 1)
        } catch (err) {
          if (!cancelRef.current && active) {
            setError(`레슨 "${lesson.lessonName}" 로딩 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
          }
          break
        }
      }
      setIsLoadingLesson(false)
    }

    loadSequentially()
    return () => { active = false }
  }, [bookDetail, allLessons, cancelled])

  return { lessonDataMap, loadedCount, totalLessons, isLoadingLesson, error, cancel, cancelled }
}

// ─── Main Component ─────────────────────────────────────────────────

export function FullBookPreviewPage() {
  const { bookId } = useParams<{ bookId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const printRef = useRef<HTMLDivElement>(null)
  const [bleedEnabled, setBleedEnabled] = useState(true)
  const [measuredMap, setMeasuredMap] = useState<Map<number, LessonMeasurement>>(new Map())
  const [autoSaveState, setAutoSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const lastSavedSignatureRef = useRef<string>('')

  const handleMeasured = useCallback((lessonId: number, m: LessonMeasurement) => {
    setMeasuredMap(prev => {
      const existing = prev.get(lessonId)
      if (existing
        && existing.contentPageCount === m.contentPageCount
        && existing.step4InlineFits === m.step4InlineFits
        && existing.measuredHeights.length === m.measuredHeights.length
        && existing.measuredHeights.every((h, i) => h === m.measuredHeights[i])
      ) {
        return prev
      }
      const next = new Map(prev)
      next.set(lessonId, m)
      return next
    })
  }, [])

  const [warningExpanded, setWarningExpanded] = useState(false)

  const { data: bookDetail, isLoading: isBookLoading } = useQuery({
    queryKey: ['print-book', bookId],
    queryFn: () => printBookApi.getById(Number(bookId!)),
    enabled: !!bookId && bookId !== 'new',
  })

  const {
    lessonDataMap,
    loadedCount,
    totalLessons,
    isLoadingLesson,
    error,
    cancel,
    cancelled,
  } = useSequentialLessonLoader(bookDetail)

  const invalidQuizzes = useMemo(
    () => collectInvalidQuizzes(lessonDataMap, bookDetail),
    [lessonDataMap, bookDetail],
  )
  const hasInvalidQuizzes = invalidQuizzes.length > 0

  const allDone = !isLoadingLesson && !isBookLoading && loadedCount === totalLessons
  const allMeasured = allDone && measuredMap.size === totalLessons && totalLessons > 0

  // Paper dimensions
  const paper = PAPER_SIZES.A4
  const pageWidthPx = mmToPx(paper.width)
  const pageHeightPx = mmToPx(paper.height)
  const bleedPx = bleedEnabled ? mmToPx(BLEED_SIZE_MM) : 0
  const totalPageWidthPx = pageWidthPx + bleedPx * 2
  const totalPageHeightPx = pageHeightPx + bleedPx * 2
  const totalPageWmm = paper.width + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
  const totalPageHmm = paper.height + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)

  // Pagination (lesson layouts + book-level content total)
  const bookPagination = useMemo<{
    lessonLayouts: Map<number, LessonLayout>
    contentTotal: number
  }>(() => {
    if (!allMeasured || !bookDetail) return { lessonLayouts: new Map(), contentTotal: 0 }
    const lessonLayouts = new Map<number, LessonLayout>()
    let currentPage = COVER_PAGE_COUNT + TOC_PAGE_COUNT + 1
    bookDetail.modules.forEach(mod => {
      if (currentPage % 2 !== 0) currentPage++
      currentPage += MODULE_SUMMARY_PAGE_COUNT
      mod.lessons.forEach(l => {
        const meas = measuredMap.get(l.lessonId)
        if (!meas) {
          console.warn(`[FullBookPreview] missing measurement for lessonId=${l.lessonId}; pagination may be incorrect`)
          return
        }
        const startPage = currentPage
        const lastContent = startPage + meas.contentPageCount - 1
        const needsMemoPage = lastContent % 2 === 0
        const totalPageCount = meas.contentPageCount + (needsMemoPage ? 1 : 0)
        lessonLayouts.set(l.lessonId, {
          startPage,
          contentPageCount: meas.contentPageCount,
          needsMemoPage,
          totalPageCount,
        })
        currentPage = startPage + totalPageCount
      })
    })
    return { lessonLayouts, contentTotal: currentPage - 1 }
  }, [allMeasured, bookDetail, measuredMap])

  const lessonLayoutMap = bookPagination.lessonLayouts

  const trailingPages = useMemo(() => {
    if (!allMeasured || !bookDetail || bookPagination.contentTotal === 0) return null
    const afterLogo = bookPagination.contentTotal + 1
    const needBlankPair = afterLogo % 4 !== 0
    const grandTotal = afterLogo + (needBlankPair ? 2 : 0)
    return { needBlankPair, grandTotal }
  }, [allMeasured, bookDetail, bookPagination.contentTotal])

  const tocModules: TocModuleData[] = useMemo(() => {
    if (!bookDetail) return []
    return bookDetail.modules.map((mod, mi) => ({
      chapterNumber: mod.chapterNumber,
      moduleName: mod.titleOverride || mod.bareTitle || mod.moduleName || `Module ${mod.chapterNumber}`,
      colorIndex: Math.min(mi, 2),
      lessons: mod.lessons.map((l, li): TocLesson => {
        const parsed = l.grade ? parseGradeField(l.grade) : null
        return {
          lessonName: l.bareTitle || l.lessonName || `레슨 ${li + 1}`,
          startPage: lessonLayoutMap.get(l.lessonId)?.startPage ?? 0,
          grade: parsed?.grade || '',
          unitInfo: parsed ? `${parsed.unitLabel} ${parsed.unitName}` : '',
        }
      }),
    }))
  }, [bookDetail, lessonLayoutMap])

  const savePagesMutation = useMutation({
    mutationFn: (request: PrintBookPagesUpdateRequest) =>
      printBookApi.updatePages(Number(bookId), request),
    onSuccess: () => {
      setAutoSaveState('saved')
      queryClient.invalidateQueries({ queryKey: ['print-book', bookId] })
    },
    onError: () => setAutoSaveState('error'),
  })

  useEffect(() => {
    if (!allMeasured || !bookDetail || !bookId || bookId === 'new') return
    // 외부 제작본은 페이지를 수동 입력으로 관리 — 프리뷰 측정값 autosave 가 수동 데이터를 덮어쓰면 안 됨
    if (bookDetail.sourceType === 'EXTERNAL_DESIGN') return
    if (lessonLayoutMap.size === 0) return
    if (savePagesMutation.isPending) return

    const payloadLessons: PrintBookPagesUpdateRequest['lessons'] = []
    bookDetail.modules.forEach(mod => {
      mod.lessons.forEach(l => {
        const layout = lessonLayoutMap.get(l.lessonId)
        const measurement = measuredMap.get(l.lessonId)
        const lessonData = lessonDataMap.get(l.lessonId)
        if (!layout || !measurement || !lessonData) return

        const blockQuizStartPage = calculateBlockQuizStartPage({
          lessonStartPage: layout.startPage,
          stepPages: measurement.stepPages,
          hasBlockQuizzes: (lessonData.steps[1]?.length ?? 0) > 0,
        })
        payloadLessons.push({
          lessonId: l.lessonId,
          pageCount: layout.contentPageCount,
          startPage: layout.startPage,
          hasMemoPage: layout.needsMemoPage,
          blockQuizStartPage,
          updateBlockQuizStartPage: true,
        })
      })
    })
    // 일부 레슨의 로딩/측정값이 빠진 상태에서는 페이지 메타를 부분 저장하지 않는다.
    if (payloadLessons.length !== totalLessons) return

    const signature = JSON.stringify(payloadLessons)
    if (signature === lastSavedSignatureRef.current) return

    setAutoSaveState('saving')
    const timer = setTimeout(() => {
      lastSavedSignatureRef.current = signature
      savePagesMutation.mutate({ totalPageCount: 0, lessons: payloadLessons })
    }, 500)
    return () => clearTimeout(timer)

  }, [allMeasured, bookDetail, bookId, lessonLayoutMap])

  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    documentTitle: bookDetail ? `${bookDetail.bookLabel}_전체` : '전체_프리뷰',
    pageStyle: `
      @page { size: ${totalPageWmm}mm ${totalPageHmm}mm; margin: 0; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print-page { page-break-after: always !important; break-after: page !important; position: relative !important; }
        .print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
      }
    `,
  })

  const progressPercent = totalLessons > 0 ? Math.round((loadedCount / totalLessons) * 100) : 0

  return (
    <div className="flex flex-col h-full">
      {/* Sticky Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white sticky top-0 z-10 print:hidden">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/contents/books/${bookId}?tab=3`)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" /> 돌아가기
          </button>

          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="font-medium text-slate-700">{bookDetail?.title || '전체 프리뷰'}</span>
            <span className="text-slate-400">|</span>
            {isLoadingLesson ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500" />
                레슨 로딩: {loadedCount}/{totalLessons}
              </span>
            ) : error ? (
              <span className="text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> 오류 발생
              </span>
            ) : allDone && !allMeasured ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
                페이지 측정 중... ({measuredMap.size}/{totalLessons})
              </span>
            ) : allMeasured ? (
              <span className="flex items-center gap-2">
                {hasInvalidQuizzes ? (
                  <span className="text-red-600 font-medium">인쇄 불가 — sample value/answersorder 누락</span>
                ) : (
                  <span className="text-green-600 font-medium">전체 준비 완료</span>
                )}
                {autoSaveState === 'saving' && (
                  <span className="flex items-center gap-1 text-slate-500">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    DB 저장 중...
                  </span>
                )}
                {autoSaveState === 'saved' && (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    페이지 정보 저장됨
                  </span>
                )}
                {autoSaveState === 'error' && (
                  <span className="flex items-center gap-1 text-red-500">
                    <AlertCircle className="w-3.5 h-3.5" />
                    저장 실패
                  </span>
                )}
              </span>
            ) : cancelled ? (
              <span className="text-slate-500">로딩 중단됨 ({loadedCount}/{totalLessons})</span>
            ) : null}
          </div>

          {/* Progress Bar */}
          {totalLessons > 0 && !allMeasured && (
            <div className="flex items-center gap-2">
              <div className="w-32 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-xs text-slate-400">{progressPercent}%</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {hasInvalidQuizzes && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setWarningExpanded(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
              >
                <AlertCircle className="w-4 h-4" />
                <span>sample value/answersorder 누락 {invalidQuizzes.length}개</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${warningExpanded ? 'rotate-180' : ''}`} />
              </button>
              {warningExpanded && (
                <div className="absolute right-0 top-full mt-1 w-96 max-h-80 overflow-auto bg-white border border-red-200 rounded-lg shadow-lg z-20 p-2">
                  <p className="text-xs text-slate-600 px-2 py-1 border-b border-slate-100 mb-1">
                    아래 퀴즈는 sample value 또는 answersorder가 등록되지 않아 인쇄가 불가합니다.
                  </p>
                  <InvalidQuizList quizzes={invalidQuizzes} />
                </div>
              )}
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-600">
            도련 (3mm)
            <button
              type="button"
              role="switch"
              aria-checked={bleedEnabled}
              onClick={() => setBleedEnabled(!bleedEnabled)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                bleedEnabled ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                bleedEnabled ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </label>

          {isLoadingLesson && !cancelled && (
            <button
              onClick={cancel}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
            >
              <XCircle className="w-4 h-4" /> 중단
            </button>
          )}

          <button
            onClick={() => reactToPrintFn()}
            disabled={!allMeasured || hasInvalidQuizzes}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {hasInvalidQuizzes ? (
              <><AlertCircle size={16} /> 인쇄 불가</>
            ) : isLoadingLesson ? (
              <><Loader2 size={16} className="animate-spin" /> 로딩 중...</>
            ) : !allMeasured ? (
              <><Loader2 size={16} className="animate-spin" /> 준비 중...</>
            ) : (
              <><Printer size={16} /> 인쇄 (PDF)</>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto flex justify-center py-6 bg-slate-100">
        {/* Hidden Measurers (off-screen, mount once data is loaded) */}
        <div aria-hidden style={{ position: 'absolute', left: -9999, top: 0 }}>
          {Array.from(lessonDataMap.values()).map(lesson => (
            <LessonMeasurer
              key={`meas-${lesson.lessonId}`}
              lesson={lesson}
              pageWidthPx={pageWidthPx}
              pageHeightPx={pageHeightPx}
              onMeasured={handleMeasured}
            />
          ))}
        </div>
        {isBookLoading ? (
          <div className="flex gap-2 items-center py-12 text-slate-500">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">교재 정보 로딩 중...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-4 text-red-700 bg-red-50 rounded-lg border border-red-200 self-start">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        ) : bookDetail ? (
          <div className="flex flex-col gap-4 items-center">
            {/* Printable Area */}
            <div
              ref={printRef}
              className="bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0"
              style={{ width: `${totalPageWidthPx}px`, lineHeight: 1.6 }}
            >
              <style>{`
                .print-page { overflow: hidden; box-sizing: border-box; height: ${totalPageHeightPx}px; }
              `}</style>

              {/* 1. Cover Page */}
              {bookDetail.coverImageUrl ? (
                <ImageCoverPage
                  src={bookDetail.coverImageUrl}
                  bleedEnabled={bleedEnabled}
                  pageWidthPx={pageWidthPx}
                  pageHeightPx={pageHeightPx}
                  totalPageWidthPx={totalPageWidthPx}
                  totalPageHeightPx={totalPageHeightPx}
                  bleedPxAlways={mmToPx(BLEED_SIZE_MM)}
                />
              ) : (
                <CoverPage
                  bookTitle={bookDetail.title}
                  bookLabel={bookDetail.bookLabel}
                  grade=""
                  pageWidth={pageWidthPx}
                  pageHeight={pageHeightPx}
                />
              )}

              {/* 2. TOC */}
              {allMeasured && (
                <TocPage
                  modules={tocModules}
                  pageWidth={pageWidthPx}
                  pageHeight={pageHeightPx}
                  bleedPx={bleedPx}
                />
              )}

              {/* 3. Modules + Lessons */}
              {bookDetail.modules.map((mod, mi) => (
                <div key={`module-${mod.moduleId}`}>
                  {/* Module Summary */}
                  <ModuleSummaryPage
                    moduleIndex={mi}
                    chapterNumber={mod.chapterNumber}
                    moduleName={mod.titleOverride || mod.bareTitle || mod.moduleName || `Module ${mod.chapterNumber}`}
                    lessons={mod.lessons.map(l => ({
                      lessonName: l.bareTitle || l.lessonName || '',
                      pageCount: lessonLayoutMap.get(l.lessonId)?.totalPageCount ?? null,
                    }))}
                    pageWidth={pageWidthPx}
                    pageHeight={pageHeightPx}
                    bleedPx={bleedPx}
                  />

                  {/* Lessons */}
                  {mod.lessons.map(lesson => {
                    const renderData = lessonDataMap.get(lesson.lessonId)
                    const layout = lessonLayoutMap.get(lesson.lessonId)
                    const meas = measuredMap.get(lesson.lessonId)
                    if (!renderData || !layout || !meas) {
                      return (
                        <div
                          key={`placeholder-${lesson.lessonId}`}
                          className="print-page relative flex items-center justify-center"
                          style={{ width: totalPageWidthPx, height: totalPageHeightPx }}
                        >
                          <div className="text-center text-slate-400">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                            <p className="text-sm"><ContentMetadataText text={lesson.bareTitle || lesson.lessonName} /> 준비 중...</p>
                          </div>
                        </div>
                      )
                    }
                    return (
                      <LessonRenderer
                        key={`render-${lesson.lessonId}`}
                        lesson={renderData}
                        startPage={layout.startPage}
                        needsMemoPage={layout.needsMemoPage}
                        stepPages={meas.stepPages}
                        step4InlineFits={meas.step4InlineFits}
                        pageHeightPx={pageHeightPx}
                        totalPageWidthPx={totalPageWidthPx}
                        totalPageHeightPx={totalPageHeightPx}
                        bleedPx={bleedPx}
                      />
                    )
                  })}
                </div>
              ))}

              {/* 4. Trailing pages — Logo + optional blanks for multiple-of-4 */}
              {trailingPages && (
                <>
                  <LogoPage
                    logoSrc="/book-logo.svg"
                    pageWidth={totalPageWidthPx}
                    pageHeight={totalPageHeightPx}
                  />
                  {trailingPages.needBlankPair && (
                    <>
                      <BlankPage pageWidth={totalPageWidthPx} pageHeight={totalPageHeightPx} />
                      <BlankPage pageWidth={totalPageWidthPx} pageHeight={totalPageHeightPx} />
                    </>
                  )}
                </>
              )}
            </div>

            {/* Loading indicator at bottom */}
            {isLoadingLesson && (
              <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                다음 레슨 로딩 중... ({loadedCount}/{totalLessons})
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
